import { startOfDay, endOfDay, format } from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';
import usStatesTerritories from './data/us-states-territories.json';
import bootstrapIconsList from './data/bootstrap-icon-list.json';
import {
    TenantUserStatusEnum,
    LogEventTypeSeverity,
    tenantTierLabels,
    NOTIFICATION_METHOD,
    TenantTiers,
} from './data/enums';
import { convertUTCDateTimeToLocalDateTime } from '../components/helpers/DateUtils';
import { logEvent } from '../services/loggingService';
import { fetchAgencies } from '../services/systemService';
import { ROUTES } from '../constants/routes';
import { ReactComponent as GoldTierIcon } from '../components/ui/svg/GoldTierIcon.svg';
import { ReactComponent as SilverTierIcon } from '../components/ui/svg/SilverTierIcon.svg';
import { ReactComponent as BronzeTierIcon } from '../components/ui/svg/BronzeTierIcon.svg';
import { ReactComponent as BaseTierIcon } from '../components/ui/svg/BaseTierIcon.svg';
import { getText } from '../i18n';
/**
 * @file utils.js
 * @description Collection of reusable functions, classes, or methods that do not fit into an exclusive use-case.
 */

/**
 * Parses a JSON Web Token (JWT) and returns the decoded payload.
 * @param {string} token - The JWT to be parsed.
 * @returns {object} - The decoded payload as a JavaScript object.
 */
export function parseJwt(token) {
    var base64Url = token.split('.')[1];
    var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    var jsonPayload = decodeURIComponent(
        window
            .atob(base64)
            .split('')
            .map(function (c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            })
            .join(''),
    );

    return JSON.parse(jsonPayload);
}

/**
 * Reads a cookie value by name from `document.cookie`.
 * @param {string} name - The cookie name.
 * @returns {string|null} - The decoded cookie value, or null if not present.
 */
export function getCookieValue(name) {
    const match = document.cookie.match(
        new RegExp(
            `(?:^|; )${name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1')}=([^;]*)`,
        ),
    );
    return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Resolves the active companyCode from, in order of precedence: the current
 * URL's query params, the `companyCode` cookie set by the server, then the
 * previously-stored value in localStorage. Used so PWA cold starts (which
 * often reopen at a bare `/` with no query string) still resolve the correct
 * client branding instead of falling back to the generic iNGAGED brand.
 * @returns {string|null} - The resolved companyCode, or null if none found.
 */
export function resolveCompanyCode() {
    const queryParams = new URLSearchParams(window.location.search);
    return (
        queryParams.get('companyCode') ||
        queryParams.get('companycode') ||
        queryParams.get('clientcode') ||
        getCookieValue('companyCode') ||
        localStorage.getItem('companyCode') ||
        null
    );
}

/**
 * Retrieves the US states and territories data.
 * @returns {Array} - The array of US states and territories.
 */
export function getUsStatesTerritories() {
    return usStatesTerritories;
}

export function getBootstrapIcons() {
    return bootstrapIconsList;
}

/**
 * Converts a color string (HEX or color name) to an RGB object.
 *
 * @param {string} colorString - The color string (e.g., "#FF0000", "green").
 * @returns {Object|null} An object with r, g, b properties or null if invalid color.
 */
export const hexToRgb = colorString => {
    // Check if it's a valid HEX color
    let hex = colorString;
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    hex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
    const hexResult = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

    if (hexResult) {
        return {
            r: parseInt(hexResult[1], 16),
            g: parseInt(hexResult[2], 16),
            b: parseInt(hexResult[3], 16),
        };
    }

    // If not HEX, try to convert color name to RGB
    const tempElement = document.createElement('div');
    tempElement.style.color = colorString;
    document.body.appendChild(tempElement);
    const rgbString = window.getComputedStyle(tempElement).color;
    document.body.removeChild(tempElement);

    const rgbResult = rgbString.match(/\d+/g);
    if (rgbResult && rgbResult.length === 3) {
        return {
            r: parseInt(rgbResult[0], 10),
            g: parseInt(rgbResult[1], 10),
            b: parseInt(rgbResult[2], 10),
        };
    }

    return null;
};

/**
 * Darkens an RGB color by a specified amount.
 *
 * @param {Object} rgb - The RGB color object with r, g, b properties.
 * @param {number} amount - The amount to darken (0-1, where 0 is no change and 1 is black).
 * @returns {Object} The darkened RGB color object.
 */
export const darkenColor = (rgb, amount) => {
    const { r, g, b } = rgb;
    return {
        r: Math.max(Math.floor(r * (1 - amount)), 0),
        g: Math.max(Math.floor(g * (1 - amount)), 0),
        b: Math.max(Math.floor(b * (1 - amount)), 0),
    };
};

/**
 * @param {string} name - the name of the object being duplicated
 * @param {[string]} existingNames  - collection names of the same object type
 * @returns name name
 */
export function getNameForDuplicate(name, existingNames) {
    let increment = 1;
    let updatedName = name;

    while (existingNames.includes(updatedName)) {
        updatedName = name + ` - ${increment}`;
        increment++;
    }
    return updatedName;
}

export function customMonthOptions() {
    return [
        { id: 1, name: 'January (1)', code: '1' },
        { id: 2, name: 'February (2)', code: '2' },
        { id: 3, name: 'March (3)', code: '3' },
        { id: 4, name: 'April (4)', code: '4' },
        { id: 5, name: 'May (5)', code: '5' },
        { id: 6, name: 'June (6)', code: '6' },
        { id: 7, name: 'July (7)', code: '7' },
        { id: 8, name: 'August (8)', code: '8' },
        { id: 9, name: 'September (9)', code: '9' },
        { id: 10, name: 'October (10)', code: '10' },
        { id: 11, name: 'November (11)', code: '11' },
        { id: 12, name: 'December (12)', code: '12' },
    ];
}

/**
 * Lightens a color by a specified amount.
 * @param {string} color - The color to lighten in hex format (e.g., #1366ff).
 * @param {number} amount - The amount to lighten the color (0 to 1).
 * @returns {string} The lightened color in hex format.
 */
export function lightenColor(color, amount) {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    const lightenedR = Math.round(r + (255 - r) * amount);
    const lightenedG = Math.round(g + (255 - g) * amount);
    const lightenedB = Math.round(b + (255 - b) * amount);

    const lightenedHex = (
        '#' +
        lightenedR.toString(16).padStart(2, '0') +
        lightenedG.toString(16).padStart(2, '0') +
        lightenedB.toString(16).padStart(2, '0')
    ).toUpperCase();

    return lightenedHex;
}

export const convertJSONToCSV = jsonPayload => {
    const headers = Object.keys(jsonPayload[0]).join(',');
    const rows = jsonPayload.map(row => Object.values(row).join(','));
    return [headers, ...rows].join('\n');
};

export function downloadCSV(jsonPayload, filename) {
    const csv = convertJSONToCSV(jsonPayload);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
}

/**
 * Returns true/false to determine if the given string is a valid URL.
 * @param {string} url - A website string.
 * @returns {boolean} Returns true or false if the given url is a valid website using the URL constructor.
 */
export function isValidUrl(url) {
    try {
        new URL(url);
        return true;
    } catch (error) {
        return false;
    }
}

/**
 * Returns true when the given value is a string containing non-whitespace characters.
 * @param {*} value - The value to check.
 * @returns {boolean} True if the value is a non-blank string.
 */
export function hasText(value) {
    return typeof value === 'string' && value.trim() !== '';
}

export function formatDate(dateString) {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('T')[0].split('-');
    return `${month}-${day}-${year}`;
}

export function formatName(name) {
    if (name?.includes(',')) {
        const [lastName, firstName] = name.split(',').map(part => part.trim());
        return `${firstName} ${lastName}`;
    }
    return name;
}

/**
 * Sanitizes a file name by replacing spaces and other problematic characters
 * @param {string} fileName - The original file name
 * @returns {string} - The sanitized file name
 */
export function sanitizeFileName(fileName) {
    if (!fileName) return fileName;
    return fileName
        .replace(/\s+/g, '_')
        .replace(/[^\w.-]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '');
}

/**
 * Formats a zip code string by removing non-digit and non-dash characters and applying standard ZIP+4 format
 * @param {string} value - The input zip code string to format
 * @returns {string} The formatted zip code string in the format: XXXXX or XXXXX-XXXX
 * @example
 * formatZipCode('12345') // returns '12345'
 * formatZipCode('123456789') // returns '12345-6789'
 * formatZipCode('12345-6789') // returns '12345-6789'
 */
export const formatZipCode = value => {
    const sanitizedValue = value.replace(/[^\d-]/g, '');
    let formattedZip = sanitizedValue;
    if (sanitizedValue.length > 5) {
        const [first, ...rest] = sanitizedValue.includes('-')
            ? sanitizedValue.split('-')
            : [sanitizedValue.slice(0, 5), sanitizedValue.slice(5)];
        formattedZip = `${first.slice(0, 5)}-${rest.join('').slice(0, 4)}`;
    }
    return formattedZip;
};

export const formatPhoneNumber = phoneNumber => {
    const sanitizedPhoneNumber = phoneNumber.replace(/[^\d-]/g, '');

    if (sanitizedPhoneNumber.length === 10) {
        return sanitizedPhoneNumber.replace(
            /(\d{3})(\d{3})(\d{4})/,
            '($1) $2-$3',
        );
    } else if (sanitizedPhoneNumber.length === 11) {
        return sanitizedPhoneNumber.replace(
            /(\d{1})(\d{3})(\d{3})(\d{4})/,
            '($2) $3-$4',
        );
    } else {
        return phoneNumber;
    }
};

export const checkForCommonValue = (arr1, arr2) => {
    for (let el of arr1) {
        if (arr2.includes(el)) {
            return true;
        }
    }
    return false;
};

//Function to capitalize the first letter of a string
export const capitalizeFirstLetter = str => {
    if (!str) return str; //Return the original string if it's empty or null
    return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Returns the property name from TenantUserStatusEnum given its numeric value,
 * with camel case split into separate words.
 * @param {number} statusId - The numeric status id.
 * @returns {string|null} The formatted property name or null if not found.
 */

export function getTenantUserStatusName(statusId) {
    for (const key in TenantUserStatusEnum) {
        if (`${TenantUserStatusEnum[key]}` === `${statusId}`) {
            const formattedKey = key.replace(/([a-z])([A-Z])/g, '$1 $2');
            return formattedKey;
        }
    }
    return null;
}

export const getStatusClass = statusId => {
    switch (`${statusId}`) {
        case '0':
            return 'ing-badge-inv-sent';
        case '1':
            return 'ing-badge-inv-sent';
        case '2':
            return 'ing-badge-pending';
        case '3':
            return 'ing-badge-active';
        case '4':
            return 'ing-badge-inactive';
        case '5':
            return 'ing-badge-disabled';
        case '6':
            return 'ing-badge-error';
        default:
            return 'ing-badge-inactive';
    }
};

/**
 * Normalizes a phone number by prepending a "+" if it starts with "1"
 * and does not already start with "+".
 * @param {string} phoneNumber The phone number to normalize.
 * @returns {string} The normalized phone number.
 */
export function parsePhoneNumber(phoneNumber) {
    if (!phoneNumber) return null;
    const trimmedNumber = phoneNumber.trim();
    if (!trimmedNumber) return null;
    if (trimmedNumber.startsWith('+')) return trimmedNumber;
    if (/^\d{10}$/.test(trimmedNumber)) return `+1${trimmedNumber}`;
    if (trimmedNumber.startsWith('1')) return `+${trimmedNumber}`;
    return trimmedNumber;
}

/**
 * Returns an array with a max number of items and sorts the array in descending order based on the specified field value provided the fields value is a number.
 * and sorts the array in descending order
 * @param {Array} data - The array of objects to filter.
 * @param {number} numItems - The maximum number of items to return.
 * @param {string} field - The field to sort by.
 * @returns {Array} - An array of objects with the top N items sorted by the specified field in descending order
 */
export const getMaxItemsByField = (data, numItems, field) => {
    // Return an empty array for invalid input
    if (!Array.isArray(data) || typeof field !== 'string' || !field) {
        return [];
    }
    // Spread the data to avoid mutating the original array
    const itemsToSort = [...data];
    const sorted = itemsToSort.sort((a, b) => {
        if (!(field in a) || !(field in b)) {
            return 0;
        }
        return b[field] - a[field];
    });
    return sorted.slice(0, numItems);
};

/**
 * Reads a boolean client option value by option id, defaulting to true when unset.
 * @param {Array} options - The client option records returned by the client options API.
 * @param {number} optionId - The client option id to look up.
 * @returns {boolean} The resolved boolean value.
 */
export function readBoolOption(options, optionId) {
    const opt = options?.find(function byId(o) {
        return o.clientOptionId === optionId;
    });
    return opt ? opt.value !== 'false' : true;
}

/**
 * Reads a string client option value by option id, defaulting to an empty string.
 * @param {Array} options - The client option records returned by the client options API.
 * @param {number} optionId - The client option id to look up.
 * @returns {string} The resolved string value.
 */
export function readStringOption(options, optionId) {
    const opt = options?.find(function byId(o) {
        return o.clientOptionId === optionId;
    });
    return opt?.value ?? '';
}

/**
 * Checks whether a rich text HTML string has any visible, non-whitespace content.
 * @param {string} html - The HTML content to check.
 * @returns {boolean} True if the HTML contains visible text.
 */
export function hasTextContent(html) {
    if (typeof html !== 'string') {
        return false;
    }
    const stripped = html
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/gi, ' ')
        .trim();
    return stripped.length > 0;
}

/**
 * Builds the default Benefit Text WYSIWYG content in the same shape the editor serializes.
 * @returns {string} HTML for the default benefit text, colored #667085.
 */
export function getDefaultBenefitTextHtml() {
    return `<div><span style="color: rgb(102, 112, 133);">${getText('msg_25a243b14a2f')}</span></div>`;
}

export const isNonProdOrStagingEnvironment = () => {
    const deployedEnvName =
        process.env.REACT_APP_DEPLOYED_ENV_NAME?.toLowerCase() ?? 'local-dev';
    return (
        !deployedEnvName.includes('prod') &&
        !deployedEnvName.includes('staging')
    );
};

/**
 * Returns a descriptive string for a given LogEventTypeSeverity enum value.
 * @param {number} severityTypeId - The LogEventTypeSeverity enum value.
 * @returns {string} The string for the event severity type.
 */
export const getLogEventSeverityType = severityTypeId => {
    const severityTypeDescriptions = {
        [LogEventTypeSeverity.Debug]: 'Debug',
        [LogEventTypeSeverity.Info]: 'Info',
        [LogEventTypeSeverity.Error]: 'Error',
        [LogEventTypeSeverity.Critical]: 'Critical',
    };

    if (severityTypeId in severityTypeDescriptions) {
        return severityTypeDescriptions[severityTypeId];
    }
    return 'Unknown Severity Type';
};

/**
 * Capitalizes the first letter of a string.
 * @param {string} str - The string to capitalize.
 * @returns {string} The string with the first letter capitalized.
 */
export const capitalizeFirstLetterOfString = str => {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Formats event log results for display.
 * @param {Array} logEvents - Array of log event objects.
 * @returns {Array} Array of formatted log event objects.
 */
export const formatEventLogResults = logEvents =>
    logEvents.map(event => {
        return {
            ...event,
            lastName: event.userFirstName || '-',
            firstName: event.userLastName || '-',
            uniqueId: event.uniqueId || '-',
            timestamp: event.timestamp
                ? convertUTCDateTimeToLocalDateTime(
                      format(event.timestamp, 'M/d/yyyy, h:mm:ss a'),
                  )
                : '-',
            eventType: event.eventTypeName,
            severity: capitalizeFirstLetterOfString(event.severity || 'Info'),
            portalName: event.portalName || '-',
            containerName: event.containerName || '-',
            browser: event.browser || '-',
            platform: event.platform || '-',
            operatingSystem: event.operatingSystem || '-',
            message: event.message || '-',
        };
    });

/**
 * Normalizes start and end date strings to UTC ISO strings based on the users time zone.
 * @param {string} startDate - The start date given as a string.
 * @param {string} endDate - The end date given as a string.
 * @returns {{ startDateUTC: string, endDateUTC: string }} - Normalized startDate and endDate UTC ISO strings at the exact start time and exact end time of the given dates used for query parameters.
 */
export const normalizeDatesToUtc = (startDate, endDate) => {
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Set the start and end dates to be the exact start and exact end of the specified dates.
    const normalizedStartDate = startOfDay(new Date(startDate));
    const normalizedEndDate = endOfDay(new Date(endDate));

    // Format dates into UTC date format.
    const startDateUTC = fromZonedTime(normalizedStartDate, userTimeZone);
    const endDateUTC = fromZonedTime(normalizedEndDate, userTimeZone);

    return {
        startDateUTC: startDateUTC.toISOString(),
        endDateUTC: endDateUTC.toISOString(),
    };
};

/**
 * Returns the label for a given tenant tier ID.
 * @param {number} tenantTierId - The tenant tier ID.
 * @returns {string} The label for the tenant tier.
 */
export const getTenantTierLabel = tenantTierId => {
    if (tenantTierId in tenantTierLabels) {
        return tenantTierLabels[tenantTierId];
    }
    return 'Unknown';
};

/**
 * Calculates the brightness of a hex color.
 * @param {string} hex - The hex color string (e.g., "#FF0000" or "#F00").
 * @returns {number} The brightness value (0-255).
 */
export const getBrightness = hex => {
    if (!hex) return 0;

    const hexToRgbValues = hex => {
        let r = 0,
            g = 0,
            b = 0;
        if (hex.length === 4) {
            r = parseInt(hex[1] + hex[1], 16);
            g = parseInt(hex[2] + hex[2], 16);
            b = parseInt(hex[3] + hex[3], 16);
        } else if (hex.length === 7) {
            r = parseInt(hex[1] + hex[2], 16);
            g = parseInt(hex[3] + hex[4], 16);
            b = parseInt(hex[5] + hex[6], 16);
        }
        return [r, g, b];
    };

    const [r, g, b] = hexToRgbValues(hex);
    return r * 0.299 + g * 0.587 + b * 0.114;
};

/**
 * Determines the best contrast color (black or white) for a given background color.
 * @param {string} hex - The hex background color string.
 * @returns {string} "#000" for dark text or "#FFF" for white text.
 */
export const getContrastColor = hex =>
    getBrightness(hex) >= 128 ? '#000' : '#FFF';

/**
 * Determines the communication preference of the individual.
 * @param {number} notificationPreferredMethodId - An ID which represents the individual's notification preference.
 * @returns {string} The communication preference of the user. Return '-' if the user has not set a notification preference.
 */
export const getIndividualCommunicationPreference =
    notificationPreferredMethodId => {
        let communicationPreference = '-';
        if (
            notificationPreferredMethodId ===
                NOTIFICATION_METHOD.primaryEmail ||
            notificationPreferredMethodId === NOTIFICATION_METHOD.optionalEmail
        ) {
            // Handles NOTIFICATION_METHOD ids 1, 4 (email notifications)
            communicationPreference = 'Email';
        } else if (
            notificationPreferredMethodId ===
                NOTIFICATION_METHOD.primaryPhone ||
            notificationPreferredMethodId === NOTIFICATION_METHOD.optionalPhone
        ) {
            // Handles NOTIFICATION_METHOD ids 2, 5 (phone notifications through text messages)
            communicationPreference = 'Text Message';
        } else if (
            notificationPreferredMethodId ===
            NOTIFICATION_METHOD.pushNotification
        ) {
            // Handles NOTIFICATION_METHOD id 3 (push notifications)
            communicationPreference = 'Push Message';
        }
        return communicationPreference;
    };

/**
 * Creates a copy of an array.  It is a deep clone so individual object will not hold a reference
 * @param {Array} arr - The array to be coppied
 * @returns {Array} A deep clone of the array
 */
export const deepCloneArray = arr => {
    return JSON.parse(JSON.stringify(arr));
};

export const hasSequentialChars = text => {
    for (let i = 0; i <= text.length - 4; i++) {
        const charCodes = [
            text.charCodeAt(i),
            text.charCodeAt(i + 1),
            text.charCodeAt(i + 2),
            text.charCodeAt(i + 3),
        ];
        if (
            charCodes[0] + 1 === charCodes[1] &&
            charCodes[1] + 1 === charCodes[2] &&
            charCodes[2] + 1 === charCodes[3]
        )
            return true;
        if (
            charCodes[0] - 1 === charCodes[1] &&
            charCodes[1] - 1 === charCodes[2] &&
            charCodes[2] - 1 === charCodes[3]
        )
            return true;
    }
    return false;
};

export const camelCaseToWords = camelCaseString => {
    if (!camelCaseString) return '';

    const words = camelCaseString.replace(/([a-z])([A-Z])/g, '$1 $2');

    return words
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

export const deepEqual = (a, b) => {
    // Handle strict equality (covers primitives + same object references)
    if (a === b) {
        // This also covers null === null
        return true;
    }

    // Handle NaN (because NaN !== NaN)
    if (typeof a === 'number' && typeof b === 'number') {
        if (Number.isNaN(a) && Number.isNaN(b)) {
            return true;
        }
    }

    // Handle Date objects
    if (a instanceof Date && b instanceof Date)
        return a.getTime() === b.getTime();

    // If types differ, not equal
    if (typeof a !== typeof b) return false;

    // Handle arrays
    if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length) return false;

        for (let i = 0; i < a.length; i++) {
            if (!deepEqual(a[i], b[i])) return false;
        }
        return true;
    }

    // If one is array and other isn't => not equal
    if (Array.isArray(a) !== Array.isArray(b)) return false;

    // Handle plain objects (and other objects except Date which handled above)
    if (a && b && typeof a === 'object' && typeof b === 'object') {
        const aKeys = Object.keys(a);
        const bKeys = Object.keys(b);

        if (aKeys.length !== bKeys.length) return false;

        // Ensure same keys (order irrelevant)
        for (const key of aKeys) {
            if (!Object.prototype.hasOwnProperty.call(b, key)) return false;

            if (!deepEqual(a[key], b[key])) return false;
        }

        return true;
    }

    // Fallback - not equal
    return false;
};

/**
 * Navigates to a client by updating local storage, updating multitenant flag,
 * dispatching a storage event, then navigating and optionally reloading.
 */
export const handleNavigateToClient = (
    tenantId,
    companyCode,
    clientName,
    setMultitenantFlagValue = false,
    reload = true,
    location = ROUTES.ROOT,
) => {
    try {
        window.localStorage.setItem('tenantId', JSON.stringify(tenantId));
        window.localStorage.setItem('companyCode', companyCode);
        window.localStorage.setItem('clientName', clientName);
        window.localStorage.setItem(
            'multitenantFlag',
            setMultitenantFlagValue ? 'true' : 'false',
        );
        window.localStorage.setItem(
            'adminView',
            setMultitenantFlagValue ? 'false' : 'true',
        );

        window.dispatchEvent(new Event('storage'));

        if (reload) {
            window.location.href = location;
        }
    } catch (err) {
        console.error('handleNavigateToClient failed', err);
        logEvent &&
            logEvent(
                'NavigationFailure',
                `handleNavigateToClient error: ${err}`,
            );
    }
};

/**
 * Fetches agencies and navigates back to the user's client (used from header).
 */
export const handleReturnToClient = async (user, reload = true) => {
    try {
        const tenantToSet = user?.jwt?.tenant;
        const agencies = await fetchAgencies();
        const companyCode = agencies?.viewModels?.find(
            agency => agency.id === user?.jwt?.agencies?.[0],
        )?.name;

        handleNavigateToClient(
            tenantToSet,
            companyCode,
            undefined,
            true,
            reload,
            ROUTES.CLIENTS,
        );
    } catch (err) {
        console.error('handleReturnToClient failed', err);
        logEvent &&
            logEvent('NavigationFailure', `handleReturnToClient error: ${err}`);
    }
};

const getTenantTierLevel = tenantTierId => {
    switch (tenantTierId) {
        case TenantTiers.Base:
            return 10;
        case TenantTiers.Bronze:
            return 20;
        case TenantTiers.Silver:
            return 30;
        case TenantTiers.Gold:
            return 40;
        default: {
            console.error(`Unknown tenant tier: ${tenantTierId}`);
            return 0;
        }
    }
};

export const getTierIcon = tier => {
    switch (tier) {
        case TenantTiers.Base:
        case 'Base':
            return <BaseTierIcon />;
        case TenantTiers.Bronze:
        case 'Bronze':
            return <BronzeTierIcon />;
        case TenantTiers.Silver:
        case 'Silver':
            return <SilverTierIcon />;
        case TenantTiers.Gold:
        case 'Gold':
            return <GoldTierIcon />;
        default:
            return '';
    }
};

/**
 * @param {int} tenantTierId - the tenant's current tier ID
 * @param {int} minimumRequiredTenantTierId - the minimum required tier ID
 * @returns {boolean} - true if the tenant's tier meets or exceeds the required minimum, false otherwise
 */
export const clientTierMeetsRequirement = (
    tenantTierId,
    minimumRequiredTenantTierId,
) => {
    if (!tenantTierId || !minimumRequiredTenantTierId) return false;

    if (isNaN(tenantTierId) || isNaN(minimumRequiredTenantTierId)) return false;

    return (
        getTenantTierLevel(tenantTierId) >=
        getTenantTierLevel(minimumRequiredTenantTierId)
    );
};
